import React, { useState, useMemo, useEffect, useContext } from 'react';
import { ThemeProvider } from "@pixel/core";
import { createInternalTheme } from "@pixel/internal/styles";
import PageHeader from "@pixel/core/PageHeader";
import Typography from '@pixel/core/Typography';
import Paper from '@pixel/core/Paper';
import ViewContextTableIcon from '@pixel/assets/icons/ViewContextTable'
import Box from "@pixel/core/Box";
import Link from '@pixel/core/Link';
import CircularProgress from '@pixel/core/CircularProgress';
import LayoutRoot from "@pixel/internal/LayoutRoot";
import LayoutRootSection from "@pixel/internal/LayoutRootSection";
import ListBase, {
  ListSearchFilterTypes,
  ListSearchChangeProps,
  ListSearchFilterCustomProps,
  ListSearchFilterSelectSingleProps,
  ListAppliedSearchFilterProps,
} from '@pixel/core/ListBase';
import { SelectChangeEvent } from '@mui/material/Select';
import DataTable from '@pixel/core/DataTable';
import Tooltip from '@pixel/core/Tooltip';
import ActionGroup from '@pixel/core/ActionGroup';
import Button from '@pixel/core/Button';
import axios from 'axios';
import { useMultipleLocalizedText } from "../../../contexts/Localization/localizationHooks";
import { mergePreselectedAndActiveDimensions, transformAccessGroupFilterData, transformAccessGroupsResult } from '../OdsTransform';
import Breadcrumb from "@pixel/core/Breadcrumb";
import BreadcrumbItem from "@pixel/core/BreadcrumbItem";
import OdsContext from '../OdsContext';

import { IAccessGroupsGetParms, IAccessGroupsResultData, IAccessGroupsData, IAccessGroupsSearchGetParms, IBreadcrumbHierarchyObject } from '../OdsTypes';
import AddAccessGroup from './AddAccessGroup';
import { useSnackbar } from '@pixel/core/SnackbarService';
import { useConfirmation, DialogLabelSet } from '@pixel/core/ConfirmationService';
import BusinessUnitsDropdown from './BusinessUnitsDropdown';
import LocationsDropdown from './LocationsDropdown';
import DepartmentsDropdown from './DepartmentsDropdown';

export const AccessGroupList = () => {
  const overlayStyling = {
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    alignItems: 'center',
    zIndex: '1',
  }

  const {
    ADMIN_USERS,
    ACCESS_GROUPS_ADD_BUTTON,
    ACCESS_GROUPS_MANAGE_CAPTION,
    ACCESS_GROUPS_PAGE_DESCRIPTION,
    ACCESS_GROUPS_SEARCH_LABEL,
    ACCESS_GROUPS_ROWS_PER_PAGE,
    ACCESS_GROUPS_GROUP_CAPTION,
    ACCESS_GROUPS_DESCRIPTION_CAPTION,
    ACCESS_GROUPS_TOTAL_USERS_CAPTION,
    ACCESS_GROUPS_STATUS_CAPTION,
    I18N_NO_RESULTS_FOUND,
    ODS_ACTIONS_EDIT,
    ODS_ACTIONS_DUPLICATE,
    ODS_ACTIONS_MAKE_INACTIVE,
    ODS_ACTIONS_MAKE_ACTIVE,
    ODS_MAKE_BU_INACTIVATE_SUCCESSFUL,
    ODS_MAKE_BU_ACTIVATE_SUCCESSFUL,
    ODS_ACCESS_GROUP_UPDATE_FAILED,
  } = useMultipleLocalizedText()

  const snack = useSnackbar();
  const confirm = useConfirmation();
  const accessGroupContext = useContext(OdsContext);
  const theme = createInternalTheme({ darkMode: false, theme: {} });

  const ODSPageSizeSessionStorage = () =>
    JSON.parse(sessionStorage.getItem('accessGroup_pageSize'))
      ? JSON.parse(sessionStorage.getItem('accessGroup_pageSize'))
      : accessGroupContext.defaultPageSize;

  const ActionGroupsFetchParams: IAccessGroupsGetParms = useMemo(
    () => ({
      pageSize: ODSPageSizeSessionStorage(),
      pageNumber: 0,
    }),
    []
  );

  const ActionGroupsSearchParams: IAccessGroupsSearchGetParms = useMemo(
    () => ({
      pageSize: ODSPageSizeSessionStorage(),
      pageNumber: 0,
      keywords: '',
    }),
    []
  );

  //accessGroupDrawer - flag for open/closed drawer state
  const [accessGroupDrawer, setAccessGroupDrawer] = useState(null);
  //addAccessGroup - flag for adding a new access group
  const [addAccessGroup, setAddAccessGroup] = useState(null);
  //editAccessGroup - flag for editing an existing access group
  const [editAccessGroup, setEditAccessGroup] = useState(null);
  //duplicateAccessGroup - flag for duplicating an existing access group
  const [duplicateAccessGroup, setDuplicateAccessGroup] = useState(null);
  //accessGroupDrawerData - selected access group data
  const [accessGroupDrawerData, setAccessGroupDrawerData] = useState(null);

  const [showInProgress, setShowInProgress] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [noSearchResult, setNoSearchResult] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [accessGroupSearchPayload, setAccessGroupSearchPayload] = useState<IAccessGroupsSearchGetParms>(ActionGroupsSearchParams);
  const [accessGroupFetchPayload, setAccessGroupFetchPayload] = useState(ActionGroupsFetchParams);
  const [appliedFilterForPagination, setAppliedFilterForPagination] = useState(null);
  const [perPageSize, setPerPageSize] = useState(ODSPageSizeSessionStorage());
  const [results, setResults] = useState<IAccessGroupsResultData>({ nodes: [], totalCount: 0, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
  const [pagingContext, setPagingContext] = useState({
    hideFirstLastActions: true,
    perPage: perPageSize,
    page: 1,
    first: 1,
    last: perPageSize,
  });

  const statusFilter: ListSearchFilterSelectSingleProps = {
    id: "statusFilter",
    label: "Status",
    type: "single_select",
    placeholder: 'Select Status',
    options: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ]
  }

  const businessUnitsFilter: ListSearchFilterCustomProps = {
    id: "businessUnitsFilter",
    label: "Business Units",
    placeholder: 'Select Business Unit',
    type: ListSearchFilterTypes.Custom,
    renderCustomFilterCallback: BusinessUnitsDropdown,
  }

  const locationsFilter: ListSearchFilterCustomProps = {
    id: "locationsFilter",
    label: "Locations",
    placeholder: 'Select Locations',
    type: ListSearchFilterTypes.Custom,
    renderCustomFilterCallback: LocationsDropdown,
  }

  const departmentsFilter: ListSearchFilterCustomProps = {
    id: "departmentsFilter",
    label: "Departments",
    placeholder: 'Select Departments',
    type: ListSearchFilterTypes.Custom,
    renderCustomFilterCallback: DepartmentsDropdown,
  }

  const [filtersApplied, setFiltersApplied] = useState(false);
  const searchFilters = [businessUnitsFilter, locationsFilter, departmentsFilter, statusFilter];
  const [appliedFilter, setAppliedFilter] = useState<ListAppliedSearchFilterProps>();
  const [accessGroupFilterPayload, setAccessGroupFilterPayload] = useState({});

  const setAccessGroupsListOnActions = () => {
    if (searchText !== '') {
      setAccessGroupFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
    }
  }

  const fetchAccessGroup = async (accessGroupFetchPayload: IAccessGroupsGetParms) => {
    try {
      setPerPageSize(ODSPageSizeSessionStorage());
      setAppliedFilter([]);
      setSearchText('');
      setListLoading(true);
      await axios
        .get(window.jvUrls.admin + '/action/accessGroups', {
          params: {
            ...accessGroupFetchPayload,
            pageSize: ODSPageSizeSessionStorage(),
          },
        })
        .then(function (response) {
          setResults(transformAccessGroupsResult(response.data));
          response.data.accessGroups.length > 0 && setNoSearchResult(false);
          setPagingContext({
            hideFirstLastActions: true,
            perPage: response.data.pageSize,
            page: response.data.pageNumber + 1,
            first:
              response.data.accessGroups.length > 0
                ? response.data.pageNumber * response.data.pageSize + 1
                : 0,
            last:
              response.data.pageSize * response.data.pageNumber +
              response.data.accessGroups.length,
          });
          setListLoading(false);
        })
        .catch(function (error) {
          setNoSearchResult(true);
          console.log(error);
          setListLoading(false);
        });
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchAccessGroup(accessGroupFetchPayload);
    localStorage.removeItem('AG_location_list');
    localStorage.removeItem('AG_department_list');
    localStorage.removeItem('AG_businessUnit_list');
    localStorage.removeItem('namedLocationsData');
    localStorage.removeItem('namedDepartmentsData');
    localStorage.removeItem('namedBusinessUnitsData');
  }, [JSON.stringify(accessGroupFetchPayload)]);

  useEffect(() => {
    if (!accessGroupDrawer) {
      setAddAccessGroup(null);
      setEditAccessGroup(null);
      setDuplicateAccessGroup(null);
      fetchAccessGroup(accessGroupFetchPayload);
    }
  }, [accessGroupDrawer]);

  const getAccessGroupFilterParams = () => {
    const filtersExist = 
      typeof accessGroupFilterPayload?.active === 'boolean' ||
      accessGroupFilterPayload?.departmentEIds ||
      accessGroupFilterPayload?.locationEIds ||
      accessGroupFilterPayload?.businessUnitEIds;

    let accessGroupPayload = {};
    if (filtersExist && searchText && searchText.length > 1) {
            accessGroupPayload = accessGroupFilterPayload;
            accessGroupPayload.keywords = accessGroupSearchPayload.keywords;
    } else if (searchText && searchText.length > 1) {
      accessGroupPayload = accessGroupSearchPayload;
    } else if (filtersExist) {
      accessGroupPayload = accessGroupFilterPayload;
    } else if (!filtersExist && searchText === '') {
      accessGroupPayload = {
        pageSize: ODSPageSizeSessionStorage(),
        pageNumber: 0,
      }
    };
    return accessGroupPayload;
  }

  useEffect(() => {
      axios.get(window.jvUrls.admin + '/action/accessGroups', {
        params: getAccessGroupFilterParams(),
      })
        .then(function (response) {
          setResults(transformAccessGroupsResult(response.data));
          setPagingContext({
            hideFirstLastActions: true,
            perPage: response.data.pageSize,
            page: response.data.pageNumber + 1,
            first:
              response.data.accessGroups.length > 0
                ? response.data.pageNumber * response.data.pageSize + 1
                : 0,
            last:
              response.data.pageSize * response.data.pageNumber +
              response.data.accessGroups.length,
          });
          setListLoading(false);
          response.data.totalCount == 0 ? setNoSearchResult(true) : setNoSearchResult(false);
        })
        .catch(function (error) {
          console.log(error);
          setListLoading(false);
        });
  }, [searchText, accessGroupSearchPayload, accessGroupFilterPayload]);  

  const onPaginationChange = (event: SelectChangeEvent) => {
    sessionStorage.setItem("accessGroup_pageSize", JSON.stringify(Number(event.target.value)));
    setPerPageSize(Number(event.target.value));
    if (searchText !== '') {
      setAccessGroupSearchPayload({ ...accessGroupSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(searchText) })
      setListLoading(true);
    } else if (appliedFilterForPagination) {
      setAppliedFilterParams(appliedFilterForPagination, 0);
    } else {
      setAccessGroupFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
    }
  }

  const setAppliedFilterParams = (filterData: any, pageNumber: number) => {
    setAppliedFilterForPagination(filterData);
    setAccessGroupFilterPayload({
      active: filterData?.status?.toLowerCase() === 'active' ? true : filterData?.status?.toLowerCase() === 'inactive' ? false : undefined, 
      departmentEIds: filterData?.departmentsFilter?.toString(),
      locationEIds: filterData?.locationsFilter?.toString(),
      businessUnitEIds: filterData?.businessUnitsFilter?.toString(),
      pageSize: ODSPageSizeSessionStorage(), pageNumber: pageNumber
    });
  }

  const handleAppliedFilterSearch = (props: ListSearchChangeProps) => {
    const filterSearchPayload = transformAccessGroupFilterData(props?.appliedSearchFilters);
    setAppliedFilterParams(filterSearchPayload, 0);
  };

  const handleSearch = (props: ListSearchChangeProps) => {
    setSearchText(props.q);
    if(props.q.length > 1 && props?.appliedSearchFilters?.length > 0){
      setAccessGroupSearchPayload({ ...accessGroupSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(props.q) })
      handleAppliedFilterSearch(props);
      setListLoading(true);
    } else  if (props.q.length > 1) {
      setAccessGroupSearchPayload({ ...accessGroupSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(props.q) })
      setListLoading(true);
      setAppliedFilter([]);
      setAccessGroupFilterPayload({});
    } else if (props?.appliedSearchFilters?.length > 0) {
      handleAppliedFilterSearch(props);
      setListLoading(true);
      setSearchText('');
      setAccessGroupSearchPayload({});
    } else if (props?.appliedSearchFilters?.length === 0 && props?.q === '') {
      setListLoading(true);
      setAccessGroupSearchPayload({})
      setAccessGroupFilterPayload({});
      setAppliedFilterForPagination(null);
    } else {
      setNoSearchResult(false);
      setAccessGroupsListOnActions();
    }
  };

  const onPageChanged = (event: any, currPage: number) => {
    if (searchText.length > 1) {
      setAccessGroupSearchPayload({ ...accessGroupSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
    } else if (appliedFilterForPagination) {
      setAppliedFilterParams(appliedFilterForPagination, currPage - 1);
    } else {
      setAccessGroupFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
    }
  }

  const addAccessGroupDrawer = () => {
    setAccessGroupDrawerData(null);
    setAccessGroupDrawer(true);
    setAddAccessGroup(true);
  }

  const toggleAGStatus = (node: IAccessGroupsData, status: boolean) => {
    try {
      axios
        .put(`${window.jvUrls.admin}/action/accessGroups/activation/${node.eid}`,
          { active: status },
          { headers: { 'Content-Type': 'application/json' } })
        .then(response => {
          const snackbarMesage = status ? ODS_MAKE_BU_ACTIVATE_SUCCESSFUL : ODS_MAKE_BU_INACTIVATE_SUCCESSFUL;
          snack.showSuccess(`${node.name} ${snackbarMesage}`, accessGroupContext.snackBarAutoHideTime);
          setAccessGroupDrawer(false);
          searchText !== '' && setSearchText('');
          setAccessGroupFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
          fetchAccessGroup(accessGroupFetchPayload);
        }).catch(error => {
          setShowInProgress(false);
          if (error.response.status === 400) {
            const { errorCode, message } = error.response.data;
            snack.showError(message, accessGroupContext.snackBarAutoHideTime);
          } else {
            snack.showError(`${ODS_ACCESS_GROUP_UPDATE_FAILED}`, accessGroupContext.snackBarAutoHideTime);
          }
          searchText !== '' && setSearchText('');
        })
    } catch (error) {
      snack.showError(`${ODS_ACCESS_GROUP_UPDATE_FAILED}`, accessGroupContext.snackBarAutoHideTime);
    }
  }

  const makeAccessGroupActive = (node: any) => {
    toggleAGStatus(node, true);
  }

  const makeAccessGroupInActive = (node: any) => {
    toggleAGStatus(node, false);
  }

  const editSelectedAccessGroup = (node: IAccessGroupsData) => {
    try {
      axios
        .get(window.jvUrls.admin + '/action/accessGroups/' + node.eid)
        .then(function (response) {
          mergePreselectedAndActiveDimensions(response?.data?.accessGroupLocationAssociation?.associatedLocationList, 'namedLocationsData');
          mergePreselectedAndActiveDimensions(response?.data?.accessGroupDepartmentAssociation?.associatedDepartmentList, 'namedDepartmentsData');
          mergePreselectedAndActiveDimensions(response?.data?.accessGroupBusinessUnitAssociation?.associatedBusinessUnitList, 'namedBusinessUnitsData');
          setAccessGroupDrawerData(response.data);
          setAccessGroupDrawer(true);
        })
        .catch(function (error) {
          console.log(error);
        });
    } catch (error) {
      console.log(error);
    }
  };

  const handleDuplicateAccessGroup = (node: IAccessGroupsData) => {
    editSelectedAccessGroup(node);
    setDuplicateAccessGroup(true);
  }

  const handleEditAccessGroup = (node: IAccessGroupsData) => {
    editSelectedAccessGroup(node);
    setEditAccessGroup(true);
  }

  const transformActionList = (actionList: any) => {
    const groupActions = [
      { i18Label: ODS_ACTIONS_EDIT, name: 'edit' },
      { i18Label: ODS_ACTIONS_DUPLICATE, name: 'duplicate' },
      { i18Label: ODS_ACTIONS_MAKE_ACTIVE, name: 'makeActive' },
      { i18Label: ODS_ACTIONS_MAKE_INACTIVE, name: 'makeInactive' },
    ];
    const actionLinks: any = [];
    if (actionList) {
      Object.keys(actionList).forEach((key) => {
        let filteredAction = groupActions.filter((e) => e.name === key);
        if (filteredAction.length > 0 && actionList[key] !== 'hidden' && actionList[key] !== null) {
          let actionTertiaryLink = {
            type: 'tertiary',
            label: filteredAction[0].i18Label,
            disabled: actionList[key] === 'grayout',
            onClick: (event: any, data: any) => {
              setAccessGroupFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
              filteredAction[0].name === 'duplicate' && handleDuplicateAccessGroup(data);
              filteredAction[0].name === 'edit' && handleEditAccessGroup(data);
              filteredAction[0].name === 'makeActive' && makeAccessGroupActive(data);
              filteredAction[0].name === 'makeInactive' && makeAccessGroupInActive(data);
            }
          };
          actionLinks.push(actionTertiaryLink);
        }
      });
    }
    return actionLinks;
  };

  const populateTitle = (node: any) => {
    if (node.active) {
      return <Tooltip title={node.name} arrow placement='bottom'><Link underline='hover' className='jv-access-group-title' sx={{ maxWidth: '100%' }} onClick={() => handleEditAccessGroup(node)} variant='body2'>{node.name}</Link></Tooltip>;
    } else {
      return <Tooltip title={node.name} arrow placement='bottom'><span><Typography variant='body2' className='jv-access-group-title' sx={{ maxWidth: '100%' }} disabled={!node.active}>{node.name}</Typography></span></Tooltip>;
    }
  }

  return (
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <LayoutRoot >
          <LayoutRootSection top className='jv-access-group-wrapper'>
            <LayoutRootSection top className='jv-access-group-page-header'>
              <Button key='contained' variant='contained' className='ods-header-button access-group-add-button' onClick={() => addAccessGroupDrawer()} color='primary'>{ACCESS_GROUPS_ADD_BUTTON}</Button>
              <Box className='jv-breadcrumb-wrapper '>
                <Breadcrumb>
                  <BreadcrumbItem href={window.jvUrls.admin + '/config/users/index.html#/users/introduction'} label={ADMIN_USERS} />
                  <BreadcrumbItem label='Access Groups' />
                </Breadcrumb>
                <PageHeader title={ACCESS_GROUPS_MANAGE_CAPTION} variant='subHeader'>
                  {ACCESS_GROUPS_PAGE_DESCRIPTION}
                </PageHeader>
              </Box>
            </LayoutRootSection>
            <LayoutRootSection className='jv-access-group-listbase'>
              {(showInProgress && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
              <ListBase
                className='access-group-list-base'
                showSearch
                labelSearch='Search...'
                labelSearchAria={ACCESS_GROUPS_SEARCH_LABEL}
                // @ts-ignore
                results={results}
                searchFilters={searchFilters}
                labelClose="Cancel"
                appliedSearchFilters={appliedFilter}
                onSearch={handleSearch}
                appliedSearchQuery={searchText}
                pagingContext={pagingContext}
                onPageChanged={onPageChanged}
                onPaginationChange={(event: SelectChangeEvent) => onPaginationChange(event)}
                rowsPerPageLabel={ACCESS_GROUPS_ROWS_PER_PAGE}
                resultsPerPage={perPageSize}
                perPageOptions={accessGroupContext.perPageSelectOptions}
                viewContexts={[
                  {
                    ariaLabel: 'Datatable View',
                    icon: <ViewContextTableIcon />,
                    renderResults: (viewContextProps) => {
                      if (listLoading) {
                        return (<div className='access-group-list-loader' style={{ display: 'flex', justifyContent: 'center' }}>
                          <CircularProgress size={34} />
                        </div>)
                      } else {
                        if (!noSearchResult) {
                          return (<DataTable
                            {...viewContextProps}
                            getRowID={node => node.id}
                            columns={[
                              { id: 'Group', columnName: ACCESS_GROUPS_GROUP_CAPTION, renderCell: (node: IAccessGroupsData) => populateTitle(node), minWidth: 140, maxWidth: 200 },
                              { id: 'Description', columnName: ACCESS_GROUPS_DESCRIPTION_CAPTION, renderCell: (node: any) => <Tooltip arrow title={node.description} placement='bottom'><span><Typography className='jv-access-group-desc' variant='body2' disabled={!node.active}>{node.description}</Typography></span></Tooltip>, minWidth: 220, maxWidth: 280 },
                              { id: 'TotalUsers', columnName: ACCESS_GROUPS_TOTAL_USERS_CAPTION, renderCell: (node: any) => <Tooltip arrow title={node.name} placement='bottom'><span><Typography className='jv-access-group-desc' variant='body2' disabled={!node.active}>{node.userCount}</Typography></span></Tooltip>, minWidth: 70, maxWidth: 80 },
                              { id: 'Status', columnName: ACCESS_GROUPS_STATUS_CAPTION, renderCell: (node: IAccessGroupsData) => (node.active) ? <Typography variant='body2' color='success' sx={{ color: 'green' }}>Active</Typography> : <Typography variant='body2' color='error' className='error-text-style' disabled>Inactive</Typography>, minWidth: 40, maxWidth: 50 },
                            ]}
                            renderActions={(node: any) => {
                              return (<ActionGroup id='primary-buttons-actions' className={'access-group-action-links'} actions={transformActionList(node.actions)} data={node} />)
                            }}
                          />)
                        } else {
                          return (<div className={'access-group-list-no-results'}><Paper sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}> {I18N_NO_RESULTS_FOUND}</Paper></div>)
                        }
                      }
                    }
                  }
                ]}
              />
            </LayoutRootSection>
          </LayoutRootSection>
        </LayoutRoot>
        {(accessGroupDrawer && <AddAccessGroup
          drawerState={accessGroupDrawer}
          setDrawerState={setAccessGroupDrawer}
          addAccessGroup={addAccessGroup}
          editAccessGroup={editAccessGroup}
          accessDrawerData={accessGroupDrawerData}
          duplicateAccessGroup={duplicateAccessGroup}
          setDuplicateAccessGroup={setDuplicateAccessGroup}
          makeAccessGroupActive={makeAccessGroupActive}
          makeAccessGroupInActive={makeAccessGroupInActive}
        />)}
      </ThemeProvider>
    </React.StrictMode>
  );
}

export default AccessGroupList;